/**
 * Board Utilities
 *
 * Core utilities for board position conversion, direction vectors,
 * and spatial relationships in the 8x8 Reversi game.
 */

import {
  BOARD_SIZE,
  TOTAL_SQUARES,
  DIRECTIONS,
  BoardPosition,
  BoardPosition2D,
  Point,
  PlayerColor,
  isValidPosition,
} from '@/types/game-types';

/**
 * Convert linear board position to 2D coordinates
 */
export const positionToCoords = (position: BoardPosition): BoardPosition2D => {
  if (!isValidPosition(position)) {
    throw new Error(`Invalid position: ${position}`);
  }
  return {
    row: Math.floor(position / BOARD_SIZE),
    col: position % BOARD_SIZE,
  };
};

/**
 * Convert 2D coordinates to linear board position
 */
export const coordsToPosition = (row: number, col: number): BoardPosition => {
  if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
    throw new Error(`Invalid coordinates: (${row}, ${col})`);
  }
  return row * BOARD_SIZE + col;
};

/**
 * Convert board position to pixel coordinates for rendering
 */
export const positionToPixel = (
  position: BoardPosition,
  cellSize: number,
  boardOffset: Point = { x: 0, y: 0 },
): Point => {
  const coords = positionToCoords(position);
  return {
    x: boardOffset.x + coords.col * cellSize + cellSize / 2,
    y: boardOffset.y + coords.row * cellSize + cellSize / 2,
  };
};

/**
 * Convert pixel coordinates to board position
 */
export const pixelToPosition = (
  pixel: Point,
  cellSize: number,
  boardOffset: Point = { x: 0, y: 0 },
): BoardPosition | null => {
  const col = Math.floor((pixel.x - boardOffset.x) / cellSize);
  const row = Math.floor((pixel.y - boardOffset.y) / cellSize);

  if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
    return null;
  }

  return coordsToPosition(row, col);
};

/**
 * Check if two positions are adjacent
 */
export const areAdjacent = (pos1: BoardPosition, pos2: BoardPosition): boolean => {
  if (!isValidPosition(pos1) || !isValidPosition(pos2)) {
    return false;
  }

  const coords1 = positionToCoords(pos1);
  const coords2 = positionToCoords(pos2);

  const rowDiff = Math.abs(coords1.row - coords2.row);
  const colDiff = Math.abs(coords1.col - coords2.col);

  return (rowDiff <= 1 && colDiff <= 1) && (rowDiff + colDiff > 0);
};

/**
 * Get distance between two positions
 */
export const getDistance = (pos1: BoardPosition, pos2: BoardPosition): number => {
  const coords1 = positionToCoords(pos1);
  const coords2 = positionToCoords(pos2);

  return Math.max(
    Math.abs(coords1.row - coords2.row),
    Math.abs(coords1.col - coords2.col),
  );
};

/**
 * Get all positions in a direction from a starting position
 */
export const getPositionsInDirection = (
  start: BoardPosition,
  direction: number,
): BoardPosition[] => {
  const positions: BoardPosition[] = [];
  const startCoords = positionToCoords(start);

  // Direction mappings: -9=NW, -8=N, -7=NE, -1=W, 1=E, 7=SW, 8=S, 9=SE
  const dirRow = Math.floor(direction / 3) - 1;
  const dirCol = (direction % 3) - 1;

  let currentRow = startCoords.row + dirRow;
  let currentCol = startCoords.col + dirCol;

  while (currentRow >= 0 && currentRow < BOARD_SIZE &&
         currentCol >= 0 && currentCol < BOARD_SIZE) {
    positions.push(coordsToPosition(currentRow, currentCol));
    currentRow += dirRow;
    currentCol += dirCol;
  }

  return positions;
};

/**
 * Get all neighbors of a position
 */
export const getNeighbors = (position: BoardPosition): BoardPosition[] => {
  const neighbors: BoardPosition[] = [];
  const coords = positionToCoords(position);

  for (let rowOffset = -1; rowOffset <= 1; rowOffset++) {
    for (let colOffset = -1; colOffset <= 1; colOffset++) {
      if (rowOffset === 0 && colOffset === 0) {continue;}

      const newRow = coords.row + rowOffset;
      const newCol = coords.col + colOffset;

      if (newRow >= 0 && newRow < BOARD_SIZE &&
          newCol >= 0 && newCol < BOARD_SIZE) {
        neighbors.push(coordsToPosition(newRow, newCol));
      }
    }
  }

  return neighbors;
};

/**
 * Get positions along the edges of the board
 */
export const getEdgePositions = (): BoardPosition[] => {
  const edges: BoardPosition[] = [];

  // Top and bottom rows
  for (let col = 0; col < BOARD_SIZE; col++) {
    edges.push(coordsToPosition(0, col));
    edges.push(coordsToPosition(BOARD_SIZE - 1, col));
  }

  // Left and right columns (excluding corners already added)
  for (let row = 1; row < BOARD_SIZE - 1; row++) {
    edges.push(coordsToPosition(row, 0));
    edges.push(coordsToPosition(row, BOARD_SIZE - 1));
  }

  return edges;
};

/**
 * Get corner positions
 */
export const getCornerPositions = (): BoardPosition[] => {
  return [0, 7, 56, 63];
};

/**
 * Get positions that are diagonally adjacent to corners (X-squares)
 */
export const getXSquarePositions = (): BoardPosition[] => {
  return [9, 14, 49, 54];
};

/**
 * Get positions that are adjacent to corners but not X-squares (C-squares)
 */
export const getCSquarePositions = (): BoardPosition[] => {
  return [1, 6, 8, 15, 48, 55, 57, 62];
};

/**
 * Check if a position is on the board edge
 */
export const isOnEdge = (position: BoardPosition): boolean => {
  const coords = positionToCoords(position);
  return coords.row === 0 || coords.row === BOARD_SIZE - 1 ||
         coords.col === 0 || coords.col === BOARD_SIZE - 1;
};

/**
 * Check if a position is a corner
 */
export const isCorner = (position: BoardPosition): boolean => {
  return getCornerPositions().includes(position);
};

/**
 * Check if a position is an X-square (diagonal from corner)
 */
export const isXSquare = (position: BoardPosition): boolean => {
  return getXSquarePositions().includes(position);
};

/**
 * Check if a position is a C-square (adjacent to corner)
 */
export const isCSquare = (position: BoardPosition): boolean => {
  return getCSquarePositions().includes(position);
};

/**
 * Get the board notation for a position (e.g., "a1", "h8")
 */
export const getPositionNotation = (position: BoardPosition): string => {
  const coords = positionToCoords(position);
  const colLetter = String.fromCharCode(97 + coords.col); // 'a' + col
  const rowNumber = coords.row + 1;
  return `${colLetter}${rowNumber}`;
};

/**
 * Parse board notation to position
 */
export const parsePositionNotation = (notation: string): BoardPosition | null => {
  if (notation.length !== 2) {return null;}

  const colChar = notation.charAt(0).toLowerCase();
  const rowChar = notation.charAt(1);

  const col = colChar.charCodeAt(0) - 97; // 'a' = 0
  const row = parseInt(rowChar, 10) - 1;

  if (col < 0 || col >= BOARD_SIZE || row < 0 || row >= BOARD_SIZE || isNaN(row)) {
    return null;
  }

  return coordsToPosition(row, col);
};

/**
 * Calculate direction between two positions
 */
export const getDirection = (from: BoardPosition, to: BoardPosition): number | null => {
  const fromCoords = positionToCoords(from);
  const toCoords = positionToCoords(to);

  const rowDiff = toCoords.row - fromCoords.row;
  const colDiff = toCoords.col - fromCoords.col;

  // Normalize to -1, 0, 1
  const normalizedRow = rowDiff === 0 ? 0 : (rowDiff > 0 ? 1 : -1);
  const normalizedCol = colDiff === 0 ? 0 : (colDiff > 0 ? 1 : -1);

  // Check if positions are in a straight line
  if (rowDiff !== 0 && colDiff !== 0 && Math.abs(rowDiff) !== Math.abs(colDiff)) {
    return null; // Not in a straight line
  }

  // Convert to direction index
  return (normalizedRow + 1) * 3 + (normalizedCol + 1) - 4;
};

/**
 * Get all positions between two positions (exclusive)
 */
export const getPositionsBetween = (
  from: BoardPosition,
  to: BoardPosition,
): BoardPosition[] | null => {
  const direction = getDirection(from, to);
  if (direction === null) {return null;}

  const positions: BoardPosition[] = [];
  const fromCoords = positionToCoords(from);
  const toCoords = positionToCoords(to);

  const rowStep = toCoords.row > fromCoords.row ? 1 : (toCoords.row < fromCoords.row ? -1 : 0);
  const colStep = toCoords.col > fromCoords.col ? 1 : (toCoords.col < fromCoords.col ? -1 : 0);

  let currentRow = fromCoords.row + rowStep;
  let currentCol = fromCoords.col + colStep;

  while (currentRow !== toCoords.row || currentCol !== toCoords.col) {
    positions.push(coordsToPosition(currentRow, currentCol));
    currentRow += rowStep;
    currentCol += colStep;
  }

  return positions;
};

/**
 * Create empty board
 */
export const createEmptyBoard = (): Uint8Array => {
  return new Uint8Array(TOTAL_SQUARES);
};

/**
 * Create initial Reversi board setup
 */
export const createInitialBoard = (): Uint8Array => {
  const board = createEmptyBoard();

  // Standard Reversi starting position
  board[coordsToPosition(3, 3)] = PlayerColor.WHITE;
  board[coordsToPosition(3, 4)] = PlayerColor.BLACK;
  board[coordsToPosition(4, 3)] = PlayerColor.BLACK;
  board[coordsToPosition(4, 4)] = PlayerColor.WHITE;

  return board;
};

/**
 * Clone board state
 */
export const cloneBoard = (board: Uint8Array): Uint8Array => {
  return new Uint8Array(board);
};

/**
 * Count pieces of each color on the board
 */
export const countPieces = (board: Uint8Array): { black: number; white: number; empty: number } => {
  let black = 0;
  let white = 0;
  let empty = 0;

  for (let i = 0; i < TOTAL_SQUARES; i++) {
    switch (board[i]) {
      case PlayerColor.BLACK:
        black++;
        break;
      case PlayerColor.WHITE:
        white++;
        break;
      case PlayerColor.EMPTY:
        empty++;
        break;
    }
  }

  return { black, white, empty };
};

/**
 * Get all positions occupied by a specific color
 */
export const getPositionsOfColor = (board: Uint8Array, color: PlayerColor): BoardPosition[] => {
  const positions: BoardPosition[] = [];
  for (let i = 0; i < TOTAL_SQUARES; i++) {
    if (board[i] === color) {
      positions.push(i);
    }
  }
  return positions;
};

/**
 * Check if board is full
 */
export const isBoardFull = (board: Uint8Array): boolean => {
  return !board.includes(PlayerColor.EMPTY);
};

/**
 * Convert board to string representation for debugging
 */
export const boardToString = (board: Uint8Array): string => {
  let result = '  a b c d e f g h\n';
  for (let row = 0; row < BOARD_SIZE; row++) {
    result += `${row + 1} `;
    for (let col = 0; col < BOARD_SIZE; col++) {
      const position = coordsToPosition(row, col);
      const piece = board[position];
      let symbol = '.';
      if (piece === PlayerColor.BLACK) {symbol = '●';}
      if (piece === PlayerColor.WHITE) {symbol = '○';}
      result += symbol + ' ';
    }
    result += `${row + 1}\n`;
  }
  result += '  a b c d e f g h\n';
  return result;
};